/*
  Theme: the MOBILE sticky add bar on the product page.

  ⚠️ A REMOTE CONTROL, NOT A SECOND FORM. This file owns no state. It has no
  variant lookup, no price formatting, no availability rule and no add request.
  Everything it displays arrives on the `ats:variant:change` event that
  assets/core-product.js emits at the end of its paint(), and its button does not
  add anything: it CLICKS THE REAL Add to bag, so the fetch, the cart drawer,
  the header badge and the sold-out guard are the same code path they always
  were. If this bar ever needs to know something new, the answer is another
  field on that event, never a second source of truth here.

  ⚠️ MOBILE ONLY, INERT ABOVE 750px, and torn down the moment the viewport
  crosses back. 750 is the PDP's own stacking width and the store-wide
  breakpoint: above it the buy column is pinned beside the gallery and the real
  button is essentially always in view, so a sticky duplicate would be noise.

  THE ONE BEHAVIOUR IT DOES NOT MIRROR is the premature click. The real button
  answers that by relabelling ITSELF for 1400ms, which works because the shopper
  is looking at it. Down here the sizes are off screen, so the same nudge would
  be invisible. This scrolls the size block into view, pulses it and announces
  it instead. Different feedback, identical refusal: nothing is added.

  PRESENCE IS AN IntersectionObserver ON THE REAL BUTTON, the same mechanism the
  gallery rail sync uses and for the same reason: `scrollend` is not dependable
  on iOS Safari, and a scroll listener would have to guess a threshold that the
  observer computes exactly. The bar shows when the real button leaves view and
  hides when it returns, symmetrically, with no hysteresis to tune.

  ⚠️ "OUT OF VIEW" MEANS BEHIND THE HEADER, NOT PAST THE VIEWPORT EDGE.
  Corrected 13 August 2026. The observer's root was the viewport, so the bar
  appeared only once the button had passed y=0. But the header is sticky at
  z-index 40 and 64px tall, so the button becomes INVISIBLE a whole header
  earlier: for those 64px of scroll it was hidden behind the header with no
  sticky bar showing, which is exactly the gap this bar exists to close.

  Fixed with a negative top rootMargin equal to the header's height, so the
  observer's root is the VISIBLE region rather than the viewport. The semantics
  are otherwise untouched and stay symmetric: the bar appears when the button's
  bottom edge passes the header's bottom edge, and hides at the same line on the
  way back. The trigger simply moved down the page by one header.
*/
(function () {
  'use strict';

  var MOBILE = '(max-width: 750px)';
  var PULSE_MS = 1000;
  var RESIZE_SETTLE_MS = 200;

  /* The sticky element, not the inner .core-header: the sticky lives on the
     SECTION WRAPPER on this theme (see the sticky-header note in
     assets/core-base.css, where a sticky on the inner element had zero
     travel and never worked). The announcement bar above it is static and
     scrolls away, so it masks nothing and is deliberately not counted. */
  var HEADER_SELECTOR = '.core-header-section';

  function init(root) {
    if (!root || root.dataset.stickyReady === 'true') return;

    var bar = root.querySelector('[data-pdp-sticky]');
    var realButton = root.querySelector('[data-add-button]');
    if (!bar || !realButton) return;

    root.dataset.stickyReady = 'true';

    var thumb = bar.querySelector('[data-sticky-thumb]');
    var priceEl = bar.querySelector('[data-sticky-price]');
    var colourEl = bar.querySelector('[data-sticky-colour]');
    var sizeEl = bar.querySelector('[data-sticky-size]');
    var sizeDot = bar.querySelector('[data-sticky-dot]');
    var addButton = bar.querySelector('[data-sticky-add]');
    var addLabel = bar.querySelector('[data-sticky-add-label]');
    var sizeBlock = root.querySelector('[data-size-block]');
    /* Ours, polite. Deliberately NOT the form's liveRegion: that one is
       aria-live="assertive" and Horizon owns it for add-to-cart results, so
       writing here would interrupt the shopper and race Horizon for the node. */
    var liveRegion = bar.querySelector('[data-sticky-live]');

    var mobileQuery = window.matchMedia(MOBILE);
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var bound = false;
    var observer = null;
    var headerHeight = -1;
    var resizeTimer = null;
    var pulseTimer = null;
    /* The last event we were handed. The bar repaints from this and from
       nothing else, so there is no path where the bar and the buy column can
       disagree about what is selected. */
    var latest = null;

    /* ---- clearance -------------------------------------------------------
       The bar is fixed, so it sits over the end of the page. Without this the
       footer's last line is permanently covered and no scroll position reveals
       it. Measured rather than assumed, because the height depends on the
       safe-area inset and on how the type wrapped. */
    function setClearance(on) {
      var doc = document.documentElement;
      if (on) {
        doc.style.setProperty('--core-sticky-clearance', bar.offsetHeight + 'px');
        document.body.classList.add('core-pdp-sticky-on');
      } else {
        document.body.classList.remove('core-pdp-sticky-on');
        doc.style.removeProperty('--core-sticky-clearance');
      }
    }

    function show() {
      if (bar.hidden) bar.hidden = false;
      /* One frame between un-hiding and the transform flip, or the browser
         collapses the two style changes into one and there is no transition to
         run: the bar would appear rather than slide. */
      window.requestAnimationFrame(function () {
        bar.setAttribute('data-shown', 'true');
        setClearance(true);
      });
    }

    function hide() {
      bar.removeAttribute('data-shown');
      setClearance(false);
      /* Left in the DOM and un-hidden while it slides out; `hidden` would cut
         the transition. It is off screen either way. */
    }

    /* The bar's price line, with a struck was-price when the variant is marked
       down. ⚠️ A DELIBERATE COPY of renderPrice in assets/core-product.js: same
       markup, same classes, same rebuild-from-scratch, so no empty node survives
       a switch to a full-price variant. The two are separate classic scripts
       with no shared module, so change both or neither. The bar never decides
       whether something is on sale: it renders what the event says. */
    function renderPrice(el, nowText, wasText) {
      if (!el || !nowText) return;
      el.textContent = '';
      if (wasText) {
        var was = document.createElement('s');
        was.className = 'core-pdp__price-was';
        was.textContent = wasText;
        el.appendChild(was);
        el.appendChild(document.createTextNode(' '));
      }
      var now = document.createElement('span');
      now.className = 'core-pdp__price-now';
      now.textContent = nowText;
      el.appendChild(now);
    }

    /* ---- painting, entirely from the event ------------------------------- */
    function paint(detail) {
      if (!detail) return;
      latest = detail;

      if (priceEl && detail.priceFormatted) renderPrice(priceEl, detail.priceFormatted, detail.priceWasFormatted);
      if (colourEl) colourEl.textContent = detail.colour || '';

      /* THE AWARENESS LINE. A chosen size is a value and reads as ink; an
         unchosen one is an instruction and stays muted, so "Select size" can
         never be mistaken for the name of a size. */
      if (sizeEl) {
        if (detail.awaitingSize) {
          sizeEl.textContent = 'Select size';
          sizeEl.setAttribute('data-chosen', 'false');
        } else if (detail.size) {
          sizeEl.textContent = detail.size;
          sizeEl.setAttribute('data-chosen', 'true');
        } else {
          /* A product with no size option at all: no prompt, no separator. */
          sizeEl.textContent = '';
          sizeEl.setAttribute('data-chosen', 'false');
        }
      }
      if (sizeDot) sizeDot.hidden = !(sizeEl && sizeEl.textContent);

      /* THE THUMBNAIL FOLLOWS THE GALLERY'S OWN TILES rather than a second
         image source: the event carries the media id, and the tile for that
         media is already on the page at the right crop. No second URL to keep
         in step, and nothing to fetch. */
      if (thumb && detail.mediaId) {
        var tile = root.querySelector('[data-media-id="' + detail.mediaId + '"] img');
        if (tile && tile.currentSrc) thumb.src = tile.currentSrc;
      }

      /* ⚠️ SOLD OUT IS MIRRORED, NOT RECOMPUTED, AND SO IS THE LABEL.
         `detail.label` is whatever currentLabel() in core-product.js just
         decided, which is the same string the real button is wearing. Copying
         it rather than deriving our own means the two buttons cannot disagree
         about the same selection, ever, including the case this task added:
         a chosen colour + size that resolves to an unavailable variant, where
         both now grey out and both read Sold Out.

         The transient nudge ("Select a size first") is deliberately NOT copied:
         it is not part of currentLabel(), so the bar keeps saying Add to bag
         while the real button flashes its own message. Correct, because down
         here the bar answers a premature tap by scrolling, not by relabelling.

         Awaiting a size is still NOT disabled, exactly as the real button is
         not: a live button that redirects is more use than a dead one.

         aria-disabled alongside the real `disabled` property: the property is
         what actually blocks the tap and is what the main button uses, the
         attribute is belt and braces for assistive tech that reads one and not
         the other. */
      if (addLabel && detail.label) addLabel.textContent = detail.label;
      if (addButton) {
        addButton.disabled = realButton.disabled;
        addButton.setAttribute('aria-disabled', realButton.disabled ? 'true' : 'false');
      }
    }

    /* ---- the premature click -------------------------------------------- */
    function redirectToSizes() {
      if (!sizeBlock) return;

      sizeBlock.scrollIntoView({
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });

      /* Cleared first: an unchanged textContent is not a change, so a second
         tap on an already-announced prompt would say nothing at all. */
      if (liveRegion) {
        liveRegion.textContent = '';
        window.setTimeout(function () {
          liveRegion.textContent = 'Choose a size';
        }, 50);
      }

      if (!reducedMotion.matches) {
        window.clearTimeout(pulseTimer);
        sizeBlock.removeAttribute('data-pulse');
        /* Re-trigger on a repeat tap: without the reflow read the attribute
           goes off and on inside one frame and the animation never restarts. */
        void sizeBlock.offsetWidth;
        sizeBlock.setAttribute('data-pulse', 'true');
        pulseTimer = window.setTimeout(function () {
          pulseTimer = null;
          sizeBlock.removeAttribute('data-pulse');
        }, PULSE_MS);
      }
    }

    function onAddClick() {
      /* A disabled <button> does not fire click in a real browser; this is the
         guard for a programmatic click and for anything that ever swaps
         `disabled` for `aria-disabled` alone. */
      if (addButton.disabled || addButton.getAttribute('aria-disabled') === 'true') return;
      if (latest && latest.awaitingSize) {
        redirectToSizes();
        return;
      }
      if (realButton.disabled) return;
      /* THE REAL BUTTON, CLICKED. Its own guard still runs, so if anything has
         changed since the last event this defers to it rather than to us. */
      realButton.click();
    }

    function onVariantChange(event) {
      paint(event.detail);
    }

    /* ⚠️ MEASURED, NEVER HARDCODED, AND THERE IS NO CUSTOM PROPERTY TO PREFER.
       Horizon's own --header-height / --header-group-height are computed by a
       script in layout/theme.liquid that returns early on a <header-component>
       this bespoke header does not have. Verified on the served page: all of
       them read empty. So the element is measured directly.

       Only a pinned header masks anything, so a static one measures 0 and the
       observer falls back to the plain viewport edge. */
    function measureHeader() {
      var header = document.querySelector(HEADER_SELECTOR);
      if (!header) return 0;
      var position = window.getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return 0;
      return Math.round(header.getBoundingClientRect().height);
    }

    /* ⚠️ NOT INTERSECTING MEANS ABOVE **OR BELOW**, AND CONFLATING THE TWO IS A
       BUG. The first version treated every non-intersecting entry as "scrolled
       past", so on a fresh page load, where the button sits below the fold, the
       bar slid up immediately over a product the shopper had not even scrolled
       to. It was missed because the first test scrolled the button into view
       before asserting, which skipped the one state that was wrong.

       So the side is checked. Above the visible region means the button is
       behind the header or past the top: show. Below means it is simply further
       down the page: stay hidden. rootBounds is the root AFTER rootMargin, so
       its top already is the header's bottom edge; headerHeight is the fallback
       for the rare engine that reports rootBounds null. */
    function onIntersect(entries) {
      if (!mobileQuery.matches) return;
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (entry.isIntersecting) {
          hide();
          continue;
        }
        var visibleTop = entry.rootBounds ? entry.rootBounds.top : headerHeight;
        if (entry.boundingClientRect.bottom <= visibleTop) show();
        else hide();
      }
    }

    /* Rebuilt rather than adjusted, because rootMargin is fixed at construction:
       an IntersectionObserver cannot have its margins changed after the fact. */
    function buildObserver() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      headerHeight = measureHeader();
      observer = new window.IntersectionObserver(onIntersect, {
        threshold: 0,
        rootMargin: '-' + headerHeight + 'px 0px 0px 0px',
      });
      observer.observe(realButton);
    }

    /* The header's height is not a constant: it changes with orientation, with
       a browser chrome that collapses on scroll, and with anything that ever
       makes the header taller. Re-measured on resize and the observer rebuilt
       only when the number actually moved, so a scroll-driven resize storm does
       not churn observers. */
    function onResize() {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        resizeTimer = null;
        if (!bound) return;
        if (measureHeader() !== headerHeight) buildObserver();
      }, RESIZE_SETTLE_MS);
    }

    function setup() {
      if (bound) return;
      bound = true;

      addButton.addEventListener('click', onAddClick);
      window.addEventListener('resize', onResize);
      window.addEventListener('orientationchange', onResize);

      buildObserver();

      /* Paint immediately from whatever is already selected, so the bar is
         correct the first time it slides up rather than on the next change. */
      if (latest) paint(latest);
    }

    function teardown() {
      if (!bound) return;
      bound = false;

      addButton.removeEventListener('click', onAddClick);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      if (resizeTimer) {
        window.clearTimeout(resizeTimer);
        resizeTimer = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      headerHeight = -1;
      if (pulseTimer) {
        window.clearTimeout(pulseTimer);
        pulseTimer = null;
      }
      if (sizeBlock) sizeBlock.removeAttribute('data-pulse');
      bar.removeAttribute('data-shown');
      bar.hidden = true;
      setClearance(false);
    }

    function sync() {
      if (mobileQuery.matches) setup();
      else teardown();
    }

    /* Bound at every width and gated inside, so a desktop variant change is
       recorded but changes nothing on screen. */
    root.addEventListener('core:variant:change', onVariantChange);

    /* ⚠️ ASK FOR THE CURRENT STATE, DO NOT WAIT FOR THE NEXT CHANGE.
       assets/core-product.js emits its first event while it initialises, which
       with `defer` happens before this file has executed, so the first event is
       always missed. Without this the bar slid up with an empty awareness line
       and a null `latest`, and the premature-click redirect never fired because
       it did not know a size was outstanding. Asking is order-independent;
       reordering the script tags would not be. */
    root.dispatchEvent(new CustomEvent('core:variant:request'));

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', sync);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(sync);
    }

    sync();
  }

  function boot() {
    try {
      var roots = document.querySelectorAll('.core-pdp');
      for (var i = 0; i < roots.length; i++) init(roots[i]);
    } catch (e) {
      /* No bar is a complete product page with a working Add to bag. Never take
         the PDP down for a convenience control. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
